import { useContext, useEffect, useState } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import { shallowEqual, useSelector } from 'react-redux'
import _ from 'lodash'

import { PRIVILEGES, ERROR_403_PATH } from '@/constants'
import { isProduction, isCompositorEnabled } from '@/utils'
import { ModalContext } from '@/contexts/ModalContext'
import * as views from '@/views'
import ModalError from '@/components/modals/ModalError/ModalError'
import NavigateTo from '@/components/NavigateTo'
import Toggles from '@domain/toggles/Toggles'

const PublicRoute = (props) => {
	const { component: Component, ...routeProps } = props

	return (
		<Route {...routeProps}>
			<Component />
		</Route>
	)
}

const ProtectedRoute = (props) => {
	const {
		redirectPath: redirectPathProp = '/',
		component: Component,
		allowed: allowedProp = [],
		...routeProps
	} = props

	const allowed = typeof allowedProp === 'string' ? [allowedProp] : allowedProp
	const redirectTo = {
		pathname: redirectPathProp
	}

	const logged = useSelector(state => state.auth.logged, shallowEqual)
	const accountDetails = useSelector(state => state.auth.account, shallowEqual)
	const [isAccessible, setIsAccessible] = useState(null)

	useEffect(() => {
		if (logged === false) {
			setIsAccessible(false)
		} else if (logged && allowed.length) {
			if (!accountDetails) {
				return
			}

			allowed.some((privilege) => accountDetails.privileges?.includes(PRIVILEGES[privilege]))
				? setIsAccessible(true)
				: setIsAccessible(false)
		} else if (logged) {
			setIsAccessible(true)
		} else {
			setIsAccessible(null)
		}
	}, [logged, accountDetails])

	useEffect(() => {
		if (isAccessible) {
			if (!isProduction()) {
				console.log(`User allowed to view route: ${routeProps.path}`)
			}
		} else if (isAccessible === false) {
			if (!isProduction()) {
				logged
					? console.log(`User NOT allowed to view route: '${routeProps.path}'. Redirecting to '${ERROR_403_PATH}'…`)
					: console.log(`User NOT allowed to view route: '${routeProps.path}'. Redirecting to '${redirectPathProp}'…`)
			}
		}
	}, [isAccessible])

	return (
		<Route
			{...routeProps}
			render={(props) => {
				if (isAccessible) {
					return <Component {...props} />
				} else if (isAccessible === false) {
					return logged ? <Redirect to={ERROR_403_PATH} /> : <Redirect to={redirectTo} />
				} else {
					return null
				}
			}}
		/>
	)
}

const MainRouter = () => {
	const { modalMount } = useContext(ModalContext)

	const accountDetails = useSelector(state => state.auth.account, shallowEqual)
	const error = useSelector(state => state.error, shallowEqual)

	useEffect(() => {
		if (error.details) {
			setTimeout(() => {
				modalMount(<ModalError disableBackdropClick />)
			}, 100)
		}
	}, [error.details])

	const compositorEnabled = isCompositorEnabled(accountDetails?.email)

	return (
		<>
			<NavigateTo />
			<Switch>
				<PublicRoute path="/" component={views.ProjectListView} exact />
				<PublicRoute path="/professional-area" component={views.ProjectListView} exact />

				<Route path="/create-project" exact>
					<Redirect from="/" to="/create-project/select-collection" />
				</Route>
				<PublicRoute path="/create-project/select-collection" component={views.CollectionList} exact />
				<PublicRoute path="/create-project/select-collection/:subFamilyId" component={views.CollectionList} />

				<PublicRoute path="/add-product/:subFamilyId" component={views.AddProduct} exact />
				<PublicRoute path="/add-product/:subFamilyId/:applicationLocationId" component={views.AddProduct} exact />
				<PublicRoute path="/add-product/:subFamilyId/:applicationLocationId/:model" component={views.AddProduct} exact />

				<PublicRoute path="/add-product-v2/:subFamilyId/:applicationLocationId/:model" component={views.AddProductV2} exact />

				<PublicRoute path="/preconfigs-list/:subFamilyId" component={views.PreconfigListView} exact />
				<PublicRoute path="/preconfigs-list/:subFamilyId/:applicationLocationId" component={views.PreconfigListView} exact />

				<PublicRoute path="/add-preconfig/:preconfigId" component={views.AddPreconfig} exact />

				<Redirect from="/project/:projectId/add-product" to="/project/:projectId/add-product/select-collection" exact />
				<ProtectedRoute
					path="/project/:projectId/add-product/select-collection"
					allowed="PROJECT_VIEW"
					component={views.CollectionList}
					exact
				/>
				<ProtectedRoute
					path="/project/:projectId/add-product/select-collection/:subFamilyId"
					allowed="PROJECT_VIEW"
					component={views.CollectionList}
				/>

				<ProtectedRoute path="/project/:projectId" allowed="PROJECT_VIEW" component={views.ProjectPageView} exact />
				<ProtectedRoute path="/project/:projectId/request-budget" component={views.RequestBudget} exact />
				<ProtectedRoute path="/project/:projectId/usa-request-budget" component={views.RequestBudgetUsa} exact />
				<ProtectedRoute path="/project/:projectId/generate-budget" component={views.GenerateBudget} exact />
				<ProtectedRoute path="/project/:projectId/registration-usa" component={views.ProjectRegistrationUsa} exact />
				<ProtectedRoute
					path="/project/:projectId/budget-quotation-usa"
					allowed="PROJECT_REGISTRATION"
					component={views.BudgetQuotationUsa}
					exact
				/>

				<PublicRoute
					path="/project/:projectId/product/:productId/express-configurator"
					component={views.ProductExpressConfiguratorView}
					exact />
				<PublicRoute
					path="/project/:projectId/product/:productId/attributes-configurator"
					component={views.ProductSheetView}
					exact
				/>
				<PublicRoute path="/new-product/crea-configurator" component={views.ProductCreaConfiguratorView} exact />
				<PublicRoute path="/new-product/crea-configurator/:model" component={views.ProductCreaConfiguratorView} exact />
				<PublicRoute
					path="/project/:projectId/add-product/crea-configurator"
					component={views.ProductCreaConfiguratorView}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/crea-configurator/:mode"
					component={views.ProductCreaConfiguratorView}
					exact
				/>

				<Redirect
					from="/sticks-nodrawing-configurator"
					to={{
						pathname: '/new-product/sticks-nodrawing-configurator',
						state: { allowCurrentProjects: true }
					}}
					exact
				/>
				<PublicRoute path="/new-product/sticks-nodrawing-configurator" component={views.SticksNodrawingConfigurator} exact />
				<PublicRoute
					path="/project/:projectId/add-product/sticks-nodrawing-configurator"
					component={views.SticksNodrawingConfigurator}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/sticks-nodrawing-configurator/:mode"
					component={views.SticksNodrawingConfigurator}
					exact
				/>

				<Redirect
					from="/sticks-drawing-configurator"
					to={{
						pathname: '/new-product/sticks-drawing-configurator',
						state: { allowCurrentProjects: true }
					}}
					exact
				/>
				<PublicRoute path="/new-product/sticks-drawing-configurator" component={views.SticksDrawingConfigurator} exact />
				<PublicRoute
					path="/project/:projectId/add-product/sticks-drawing-configurator"
					component={views.SticksDrawingConfigurator}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/sticks-drawing-configurator/:mode"
					component={views.SticksDrawingConfigurator}
					exact
				/>

				<Redirect
					from="/plusminus-nodrawing-configurator"
					to={{
						pathname: '/new-product/plusminus-nodrawing-configurator',
						state: { allowCurrentProjects: true }
					}}
					exact
				/>
				<PublicRoute
					path="/new-product/plusminus-nodrawing-configurator"
					component={views.PlusminusNodrawingConfigurator}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/add-product/plusminus-nodrawing-configurator"
					component={views.PlusminusNodrawingConfigurator}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/plusminus-nodrawing-configurator/:mode"
					component={views.PlusminusNodrawingConfigurator}
					exact
				/>
				<Redirect
					from="/plusminus-drawing-configurator"
					to={{
						pathname: '/new-product/plusminus-drawing-configurator',
						state: { allowCurrentProjects: true }
					}}
					exact
				/>
				<PublicRoute
					path="/new-product/plusminus-drawing-configurator"
					component={views.PlusminusDrawingConfiguratorContainer}
					exact
				/>
				<PublicRoute
					path="/plusminus-drawing-configurator/product/:hash"
					component={views.PlusminusDrawingConfiguratorWithHash}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/add-product/plusminus-drawing-configurator"
					component={views.PlusminusDrawingConfiguratorContainer}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/plusminus-drawing-configurator/:mode"
					component={views.PlusminusDrawingConfiguratorContainer}
					exact
				/>
				<PublicRoute
					path="/project/:projectId/product/:productId/export"
					component={views.Export3dContainer}
					exact
					/>
				<ProtectedRoute
					path="/validate/:projectId/:productId"
					component={views.ValidationView}
					exact
				/>

				<PublicRoute path="/orders" component={views.OrderList} />
				<ProtectedRoute path="/order/:orderId" allowed="ORDER_VIEW" component={views.OrderDetails} />
				<PublicRoute path="/project/:projectId/new-order" allowed="ORDER_VIEW" component={views.PlaceOrder} />

				<ProtectedRoute
					path="/project/:projectId/downloads"
					allowed="PROJECT_DOWNLOADS"
					component={views.ProjectDownloads}
				/>
				<ProtectedRoute
					path="/project/:projectId/technical-downloads"
					allowed="PROJECT_DOWNLOADS"
					component={views.ProjectTechnicalDownloads}
				/>
				<ProtectedRoute
					path="/project/:projectId/export-3d"
					allowed="PROJECT_DOWNLOADS"
					component={views.Export3DList}
				/>

				<PublicRoute path="/services/download" component={views.ServiceDownload}/>

				<ProtectedRoute path="/my-profile" component={views.UserProfile} exact />
				<ProtectedRoute path="/users-management" allowed="COMPANY_MANAGE_USER" component={views.UsersManagement} exact />
				<ProtectedRoute path="/invoices" allowed="ORDER_VIEW" component={views.Invoices} exact />
				<PublicRoute path="/password-reset" component={views.PasswordReset} exact />
				<Redirect from="/new-register" to="en/new-register" exact />
				<PublicRoute path="/:language/new-register" component={views.NewRegister} exact />
				<PublicRoute path="/sandbox" component={views.Sandbox} exact />

				<PublicRoute path="/compositor/v2" component={views.CompositorV2} exact />
				{!!compositorEnabled && <PublicRoute path="/studio" component={views.Studio} exact />}
				{!!compositorEnabled && <PublicRoute path="/compositor" component={views.Compositor} exact />}
				{!!compositorEnabled && <PublicRoute path="/compositor/generatePreviews" component={views.GeneratePreviews} exact />}
				{!!compositorEnabled && <PublicRoute path="/compositor/generatePdf" component={views.GeneratePdf} exact />}
				{!!compositorEnabled && <PublicRoute path="/technicalinfo" component={views.TechnicalInformation} exact />}
				{!!compositorEnabled && <PublicRoute path="/productsheet" component={views.ProductSheetView} exact />}
				<PublicRoute path="/product-admin" component={views.ProductAdmin} exact />
				<PublicRoute path="/product-admin/compose" component={views.ProductAdminCompositor} exact />
				<PublicRoute path="/product-admin/EditRule" component={views.ProductAdminEditRule} exact />
				{Toggles.isEnabled("composeUrlPreview") && <PublicRoute path="/preview/compose" component={views.ProductAdminPreview} exact />}
				<PublicRoute path="/compositor/_demo" component={views.Compositor} exact />
				<PublicRoute path="/minicompositor" component={views.MiniCompositor} exact />
				<PublicRoute path={ERROR_403_PATH} component={views.NotAllowedView} exact />
				<PublicRoute component={views.NotFoundView} />

			</Switch>
		</>
	)
}

export default MainRouter
